import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import ArrowBtn from '@components/dataEntry/button/ArrowBtn/ArrowBtn';
import useInterval from '@hooks/useInterval';
import { CollectionPath, ImainhomeDoc } from '@typings/collections.enum';
import { useDocument } from '@nandorojo/swr-firestore';
import { ImainHome } from '@typings/setting/mainHome.interface';
import { IluckydrawItem } from '@typings/store/luckydraw.interface';
import { IslideItem } from '@typings/setting/slideItem';
import SliderContents from './SliderContents';
import { IstoryItem } from '@typings/guestbook/story.interface';
import { cardBorderImg } from '@styles/common/Card';

const Wrapper = styled.div<{ $withCardFrame?: boolean }>`
  position: absolute;
  inset: 0;
  overflow: hidden;
  box-sizing: border-box;
  font-size: 0;
  line-height: 0;

  ${({ $withCardFrame }) =>
    $withCardFrame &&
    css`
      ${cardBorderImg};
      z-index: 0;
      border-radius: var(--cardBorderRadius);
      overflow: hidden;
    `}
`;

const ControlBox = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;

  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`;

const ControlBtn = styled(motion.div)`
  margin-left: 8px;
  border-radius: 12px;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
  }
`;

const SlideSec = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;

type Props = {
  list: IslideItem[];
  height?: number;
  slides?: any;
  delayLength?: number;
  autoplay?: boolean;
  showButton?: boolean;
  showArrow?: boolean;
  customContents?: ReactNode;
  style?: React.CSSProperties;
  withCardFrame?: boolean;
};

function Slider({
  list,
  height,
  delayLength,
  autoplay,
  showButton = true,
  showArrow = false,
  customContents,
  style,
  withCardFrame = false,
}: Props) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(1);
  const [percentX, setPercentX] = useState(0);
  const [pixelX, setPixelX] = useState(0);
  const [touchX, setTouchX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDown, setIsDown] = useState(false);
  const [walk, setWalk] = useState(0);
  const [isNavClick, setIsNavClick] = useState(true);

  const delay = delayLength || 5000;
  const isRunning = autoplay || true;
  const isFirstMount = useRef(true);

  const [customStyle, setStyle] = useState({
    transform: `translateX(-${current}00%)`,
    transition: `0ms`,
  });

  const [slideList, setSlideList] = useState<
    IslideItem[] | IluckydrawItem[] | IstoryItem[]
  >([]);
  const [filterShowList, setFilterShowList] = useState<
    IslideItem[] | IluckydrawItem[] | IstoryItem[]
  >([]);
  const slideLength = filterShowList.length;

  const { data: mainHome, set } = useDocument<ImainHome>(
    `${CollectionPath.MAINHOME}/${ImainhomeDoc.MAINHOME}`,
    { listen: true },
  );

  useEffect(() => {
    if (!list) return;

    const filteredShowList = list.filter((item) => item.isShow ?? true);

    setFilterShowList(filteredShowList);

    // 여기는 보여지는 slideList
    if (filteredShowList.length > 1) {
      setSlideList([
        filteredShowList[filteredShowList.length - 1],
        ...filteredShowList,
        filteredShowList[0],
      ]);
    } else {
      setSlideList(filteredShowList);
    }
  }, [list]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setStyle({
      transform: isNavClick
        ? `translateX(-${current}00%)`
        : `translateX(calc(-${current}00% + ${pixelX}px)`,
      transition: `all 1s ease-in-out`,
    });
  }, [current, pixelX, isNavClick]);

  const handlePrevSlide = useCallback(() => {
    setCurrent(current - 1);

    //옆으로 가는 슬라이드 일 떄
    setIsNavClick(true);
  }, [current, filterShowList, percentX]);

  const handleNextSlide = useCallback(() => {
    setCurrent(current + 1);

    //옆으로 가는 슬라이드 일 떄
    setIsNavClick(true);
  }, [current, filterShowList, percentX]);

  // 터치에도 반응하는 슬라이드 --------------------------------------
  const handleSlideMouseDown = useCallback(
    (e) => {
      if (filterShowList.length <= 1) return;

      e.preventDefault();

      if (sliderRef.current) {
        setTouchX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
      }

      setIsDown(true);
    },
    [sliderRef, filterShowList],
  );

  const handleSlideMouseLeave = useCallback(() => {
    setIsDown(false);
  }, []);

  const handleSlideMouseUp = useCallback(() => {
    if (filterShowList.length <= 1) return;

    if (sliderRef.current) {
      setPixelX(sliderRef.current.scrollLeft);
    }
    setIsDown(false);
    setIsNavClick(false);

    // next
    if (walk < 0) {
      handleNextSlide();
    }

    // prev
    if (walk > 0) {
      handlePrevSlide();
    }
  }, [percentX, sliderRef, walk, filterShowList]);

  function handleSlideMouseMove(e) {
    if (!isDown) {
      return;
    }
    e.preventDefault();
    if (sliderRef.current) {
      const offsetLeft = sliderRef.current.offsetLeft;
      const startX = e.pageX - offsetLeft;
      const walk = startX - touchX;

      setWalk(walk);
    }
  }
  // 터치에도 반응하는 슬라이드 end --------------------------------------

  useInterval(
    () => {
      if (filterShowList.length > 1) handleNextSlide();
    },
    isRunning ? delay : null,
  );

  useEffect(() => {
    // slideList가 아직 로드되지 않은 경우 무시
    if (slideList.length === 0) return;

    // 가장 첫번째 이미지에서 마지막(뒤로) 넘어갈 때
    if (slideList.length <= 1) {
      setCurrent(0);
      return;
    }

    if (current === 0) {
      setTimeout(function () {
        setStyle({
          transform: `translateX(-${slideList.length - 2}00%)`,
          transition: '0ms',
        });
        setCurrent(slideList.length - 2);
      }, 1100);
    }

    // 가장 마지막 이미지에서 처음으로 넘어갈 때
    if (current >= slideList?.length - 1) {
      setTimeout(() => {
        setStyle({
          transform: `translateX(-${1}00%)`,
          transition: '0ms',
        });
        setCurrent(1);
      }, 1100);
    }
  }, [current, slideList.length]);

  const showArrowBtn =
    (filterShowList.length > 1 && mainHome && mainHome.slideButtonShow) ||
    showArrow;

  function ArrowBtnSec() {
    return (
      <>
        <ArrowBtn
          direction="left"
          size={20}
          onClick={handlePrevSlide}
          style={{
            position: 'absolute',
            top: '50%',
            left: '6px',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        />
        <ArrowBtn
          direction="right"
          size={20}
          onClick={handleNextSlide}
          style={{
            position: 'absolute',
            top: '50%',
            right: '6px',
            transform: 'translateY(-50%)',
            zIndex: 1,
          }}
        />
      </>
    );
  }

  return (
    <>
      <Wrapper
        style={{ ...style }}
        $withCardFrame={
          mainHome?.slideBorderShow !== undefined
            ? mainHome.slideBorderShow
            : true
        }
      >
        {showArrowBtn && <ArrowBtnSec />}

        <SlideSec
          ref={sliderRef}
          onMouseDown={handleSlideMouseDown}
          onMouseLeave={handleSlideMouseLeave}
          onMouseUp={handleSlideMouseUp}
          onMouseMove={handleSlideMouseMove}
          style={{
            height: height,
            ...customStyle,
          }}
        >
          {slideList.map((item, index) => {
            return (
              <SliderContents
                data={item}
                key={index}
                isActive={index === current}
                showButton={showButton}
                customContents={customContents}
              />
            );
          })}
        </SlideSec>
        {slideLength > 1 && (
          <ControlBox>
            {Array.from({ length: slideLength }, (_, i) => i + 1).map(
              (_item, index) => (
                <ControlBtn
                  key={index}
                  animate={{
                    width: index === current - 1 ? 28 : 12,
                    background:
                      index === current - 1
                        ? 'var(--primaryColor)'
                        : 'var(--subColor)',
                    height: 7,
                  }}
                  onClick={() => {
                    setCurrent(index + 1);
                    setPercentX(index === 0 ? 0 : index * -100);
                  }}
                />
              ),
            )}
          </ControlBox>
        )}
      </Wrapper>
    </>
  );
}

export default Slider;
